/**
 * Выбор адреса бэкенда.
 *
 * Раньше песочница определялась через `hostname.startsWith("sandbox-admin.")`.
 * На стендовых доменах вида `pr-sandbox-admin.payfinzora.com` такая проверка
 * не срабатывает, и стенд молча уходил бы в боевой API — поэтому здесь мы
 * ищем слово "sandbox" в любой позиции имени хоста.
 *
 * Порядок приоритетов:
 *   1. REACT_APP_API_BASE_URL — жёсткое закрепление адреса на сборку;
 *   2. dev-прокси `/api` в режиме разработки (обходит CORS);
 *   3. определение по домену.
 */

/** Боевой контур. Переопределяется на сборке. */
const PROD_API =
  process.env.REACT_APP_API_URL || "https://api.payfinzora.com";

/** Песочница. Переопределяется на сборке. */
const SANDBOX_API =
  process.env.REACT_APP_SANDBOX_API_URL || "https://sandbox.payfinzora.com";

/** Совпадает с sandbox-admin, pr-sandbox-admin, sandbox-merchant и т.д. */
export const isSandboxHost = (hostname: string): boolean =>
  /(^|[.-])sandbox([.-]|$)/i.test(hostname);

export const resolveApiBaseUrl = (): string => {
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }

  if (process.env.NODE_ENV === "development") {
    return "/api";
  }

  return isSandboxHost(window.location.hostname) ? SANDBOX_API : PROD_API;
};

/**
 * Адрес websocket-ленты транзакций.
 *
 * Строится из того же базового адреса, что и обычные запросы, — иначе сокет
 * ушёл бы в другой контур, чем список. Схема всегда wss на боевых доменах:
 * кука access_token помечена Secure и по ws:// не отправится.
 *
 * В разработке база — относительный "/api", поэтому идём через dev-прокси на
 * собственный хост (там ws:// допустим, это localhost).
 */
export const resolveWsUrl = (path: string): string => {
  const base = resolveApiBaseUrl();
  const suffix = path.startsWith("/") ? path : `/${path}`;

  if (base.startsWith("http")) {
    return base.replace(/^http/, "ws").replace(/\/+$/, "") + suffix;
  }

  const scheme = window.location.protocol === "https:" ? "wss" : "ws";
  const prefix = base.replace(/\/+$/, "");
  return `${scheme}://${window.location.host}${prefix}${suffix}`;
};
